// Signage → Programación (/signage/programacion). Dayparting: qué plantilla
// corre por pantalla o grupo, según día y franja horaria (mayor prioridad gana;
// una regla de pantalla vence a una de grupo).
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { Campo, Modal, Switch, fieldCls } from '@/features/pos/shared';
import { DIAS, MOMENTOS, sigInsert, sigList, sigSoftDelete, sigUpdate } from './shared';
import type { RefRow } from './shared';

interface Prog {
  id: string; ambito: 'pantalla' | 'grupo'; pantalla_id: string | null; grupo_id: string | null;
  dia_semana: number | null; hora_inicio: string; hora_fin: string; plantilla_id: string; prioridad: number; es_activo: boolean;
  momento: string | null;   // 420: null=siempre | quincena | fin_mes | pago
}

export function Programacion() {
  const qc = useQueryClient();
  const [edit, setEdit] = useState<Partial<Prog> | null>(null);
  const [guardando, setGuardando] = useState(false);

  const { data: progs = [] } = useQuery<Prog[]>({ queryKey: ['sig-prog'], queryFn: () => sigList<Prog>('programacion?select=*&deleted_at=is.null&order=prioridad.desc') });
  const { data: pantallas = [] } = useQuery<RefRow[]>({ queryKey: ['sig-pantallas-ref'], queryFn: () => sigList<RefRow>('pantalla?select=id,nombre&deleted_at=is.null&order=nombre') });
  const { data: grupos = [] } = useQuery<RefRow[]>({ queryKey: ['sig-grupos'], queryFn: () => sigList<RefRow>('grupo?select=id,nombre&deleted_at=is.null&order=nombre') });
  const { data: plantillas = [] } = useQuery<RefRow[]>({ queryKey: ['sig-plantillas'], queryFn: () => sigList<RefRow>('plantilla?select=id,nombre&deleted_at=is.null&order=nombre') });

  const nom = (arr: RefRow[], id: string | null) => (id ? arr.find((x) => x.id === id)?.nombre ?? '—' : '—');
  const plaNom = useMemo(() => Object.fromEntries(plantillas.map((p) => [p.id, p.nombre])), [plantillas]);
  const invalidar = () => qc.invalidateQueries({ queryKey: ['sig-prog'] });

  const guardar = async () => {
    if (!edit?.plantilla_id) { toast.error('Elige la plantilla'); return; }
    if (edit.ambito === 'pantalla' && !edit.pantalla_id) { toast.error('Elige la pantalla'); return; }
    if (edit.ambito === 'grupo' && !edit.grupo_id) { toast.error('Elige el grupo'); return; }
    setGuardando(true);
    try {
      const body = {
        ambito: edit.ambito ?? 'pantalla',
        pantalla_id: edit.ambito === 'grupo' ? null : edit.pantalla_id || null,
        grupo_id: edit.ambito === 'grupo' ? edit.grupo_id || null : null,
        dia_semana: edit.dia_semana ?? null,
        hora_inicio: edit.hora_inicio || '00:00', hora_fin: edit.hora_fin || '23:59',
        plantilla_id: edit.plantilla_id, prioridad: edit.prioridad ?? 10, es_activo: edit.es_activo ?? true,
        momento: edit.momento || null,
      };
      if (edit.id) await sigUpdate('programacion', edit.id, body); else await sigInsert('programacion', body);
      setEdit(null); invalidar(); toast.success('Programación guardada');
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setGuardando(false); }
  };

  return (
    <div className="p-6 space-y-4 max-w-5xl">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><CalendarClock className="w-5 h-5" /> Programación (dayparting)</h1>
        <Button className="ml-auto" onClick={() => setEdit({ ambito: 'pantalla', dia_semana: null, hora_inicio: '00:00', hora_fin: '23:59', prioridad: 10, es_activo: true })}>
          <Plus className="w-4 h-4 mr-1" /> Nueva regla
        </Button>
      </div>
      <div className="border rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-muted-foreground border-b bg-muted/30">
            <th className="py-2 px-3 text-right w-10">N°</th><th className="py-2 px-3 text-left">Aplica a</th>
            <th className="py-2 px-3 text-left">Día</th><th className="py-2 px-3 text-left">Franja</th>
            <th className="py-2 px-3 text-left">Plantilla</th><th className="py-2 px-3 text-right">Prioridad</th><th className="py-2 px-3" />
          </tr></thead>
          <tbody>
            {progs.map((p, i) => (
              <tr key={p.id} className="border-b last:border-0">
                <td className="py-2 px-3 text-right text-muted-foreground">{i + 1}</td>
                <td className="py-2 px-3">{p.ambito === 'grupo' ? `Grupo: ${nom(grupos, p.grupo_id)}` : `Pantalla: ${nom(pantallas, p.pantalla_id)}`}</td>
                <td className="py-2 px-3">
                  {DIAS.find((d) => d.v === p.dia_semana)?.l ?? 'Todos'}
                  {p.momento && <span className="ml-1 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 px-1 text-[11px] font-medium">{p.momento === 'pago' ? 'días de pago' : p.momento === 'quincena' ? 'quincena' : 'fin de mes'}</span>}
                </td>
                <td className="py-2 px-3 font-mono">{(p.hora_inicio || '').slice(0, 5)}–{(p.hora_fin || '').slice(0, 5)}</td>
                <td className="py-2 px-3">{plaNom[p.plantilla_id] ?? '—'}</td>
                <td className="py-2 px-3 text-right">{p.prioridad}</td>
                <td className="py-2 px-3 text-right">
                  <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => setEdit(p)}><Pencil className="w-4 h-4" /></button>
                  <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Eliminar" onClick={() => sigSoftDelete('programacion', p.id).then(invalidar)}><Trash2 className="w-4 h-4 text-red-500" /></button>
                </td>
              </tr>
            ))}
            {!progs.length && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Sin reglas: cada pantalla usa su plantilla por defecto todo el día.</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">Sin regla vigente, la pantalla muestra su plantilla por defecto. Una regla de <strong>pantalla</strong> vence a una de <strong>grupo</strong>; a igualdad, gana la de mayor prioridad.</p>

      {edit && (
        <Modal title={edit.id ? 'Editar regla' : 'Nueva regla'} onClose={() => setEdit(null)}>
          <div className="space-y-3">
            <Campo label="Aplicar a">
              <select className={fieldCls} value={edit.ambito ?? 'pantalla'} onChange={(e) => setEdit({ ...edit, ambito: e.target.value as 'pantalla' | 'grupo' })}>
                <option value="pantalla">Una pantalla</option><option value="grupo">Un grupo</option>
              </select>
            </Campo>
            {edit.ambito === 'grupo' ? (
              <Campo label="Grupo">
                <select className={fieldCls} value={edit.grupo_id ?? ''} onChange={(e) => setEdit({ ...edit, grupo_id: e.target.value })}>
                  <option value="">— Selecciona —</option>{grupos.map((g) => <option key={g.id} value={g.id}>{g.nombre}</option>)}
                </select>
              </Campo>
            ) : (
              <Campo label="Pantalla">
                <select className={fieldCls} value={edit.pantalla_id ?? ''} onChange={(e) => setEdit({ ...edit, pantalla_id: e.target.value })}>
                  <option value="">— Selecciona —</option>{pantallas.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                </select>
              </Campo>
            )}
            <Campo label="Plantilla a mostrar">
              <select className={fieldCls} value={edit.plantilla_id ?? ''} onChange={(e) => setEdit({ ...edit, plantilla_id: e.target.value })}>
                <option value="">— Selecciona —</option>{plantillas.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
              </select>
            </Campo>
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Día">
                <select className={fieldCls} value={edit.dia_semana ?? ''} onChange={(e) => setEdit({ ...edit, dia_semana: e.target.value === '' ? null : Number(e.target.value) })}>
                  {DIAS.map((d) => <option key={String(d.v)} value={d.v ?? ''}>{d.l}</option>)}
                </select>
              </Campo>
              <Campo label="Prioridad"><input className={fieldCls} type="number" value={edit.prioridad ?? 10} onChange={(e) => setEdit({ ...edit, prioridad: Number(e.target.value) || 0 })} /></Campo>
              <Campo label="Momento (recurrente)">
                <select className={fieldCls} value={edit.momento ?? ''} onChange={(e) => setEdit({ ...edit, momento: e.target.value || null })}>
                  {MOMENTOS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
                </select>
              </Campo>
              <Campo label="Desde"><input className={fieldCls} type="time" value={(edit.hora_inicio || '00:00').slice(0, 5)} onChange={(e) => setEdit({ ...edit, hora_inicio: e.target.value })} /></Campo>
              <Campo label="Hasta"><input className={fieldCls} type="time" value={(edit.hora_fin || '23:59').slice(0, 5)} onChange={(e) => setEdit({ ...edit, hora_fin: e.target.value })} /></Campo>
            </div>
            <Switch checked={edit.es_activo ?? true} onChange={(v) => setEdit({ ...edit, es_activo: v })} label="Regla activa" />
            <div className="flex justify-end gap-2 pt-2"><Button variant="ghost" onClick={() => setEdit(null)}>Cancelar</Button><Button onClick={() => void guardar()} isLoading={guardando}>Guardar</Button></div>
          </div>
        </Modal>
      )}
    </div>
  );
}
